import { Notification } from '@helpers/Models';

// // Union type for notifications that can come from different sources
// type AnyNotification = Notification | NotificationData;

interface AppNotification {
  id: number;
  createdBy: number | null;
  createdAt: Date;
  modifiedBy: number | null;
  modifiedAt: Date;
  butcherId: number;
  deletedBy: number | null;
  deletedAt: Date | null;
  moduleId: number;
  message: string;
  actionKey: string;
  roleIds: number[];
  isRead: number[];
}

/**
 * Centralized Notification Management Service
 * Handles all notification-related localStorage operations and state management
 */
export class NotificationManager {
  private static readonly NOTIFICATIONS_KEY = 'notifications';
  private static readonly UNREAD_NOTIFICATIONS_KEY = 'unreadNotifications';

  /**
   * Get all notifications from localStorage
   */
  static getAllNotifications(): Notification[] {
    try {
      const notificationsStr = localStorage.getItem(this.NOTIFICATIONS_KEY);
      return notificationsStr ? JSON.parse(notificationsStr) : [];
    } catch (error) {
      console.error('Error parsing notifications from localStorage:', error);
      return [];
    }
  }

  /**
   * Get unread notifications from localStorage
   */
  static getUnreadNotifications(): Notification[] {
    try {
      const unreadNotificationsStr = localStorage.getItem(
        this.UNREAD_NOTIFICATIONS_KEY,
      );
      return unreadNotificationsStr ? JSON.parse(unreadNotificationsStr) : [];
    } catch (error) {
      console.error(
        'Error parsing unread notifications from localStorage:',
        error,
      );
      return [];
    }
  }

  /**
   * Get count of unread notifications
   */
  static getUnreadCount(): number {
    return this.getUnreadNotifications().length;
  }

  /**
   * Add a new notification (places it at the beginning of the array for latest-first order)
   * @param notification - The new notification to add (can be from socket or API)
   */
  static addNotification(notification: AppNotification): void {
    try {
      // Get existing notifications
      const allNotifications = this.getAllNotifications();
      const unreadNotifications = this.getUnreadNotifications();

      // Convert NotificationData to Notification format if needed
      // const normalizedNotification = this.normalizeNotification(notification);

      // Add new notification at the beginning (index 0) for latest-first order
      const updatedAllNotifications = [notification, ...allNotifications];
      const updatedUnreadNotifications = [notification, ...unreadNotifications];

      // Save back to localStorage
      localStorage.setItem(
        this.NOTIFICATIONS_KEY,
        JSON.stringify(updatedAllNotifications),
      );
      localStorage.setItem(
        this.UNREAD_NOTIFICATIONS_KEY,
        JSON.stringify(updatedUnreadNotifications),
      );

      // Trigger storage event for other components to update
      this.triggerStorageEvent();
    } catch (error) {
      console.error('Error adding notification:', error);
    }
  }

  /**
   * Mark specific notifications as read by removing them from unread list
   * @param notificationIds - Array of notification IDs to mark as read
   */
  static markNotificationsAsRead(notificationIds: (string | number)[]): void {
    try {
      const unreadNotifications = this.getUnreadNotifications();

      // Filter out the notifications that are being marked as read
      const updatedUnreadNotifications = unreadNotifications.filter(
        (notification) => !notificationIds.includes(notification.id),
      );

      localStorage.setItem(
        this.UNREAD_NOTIFICATIONS_KEY,
        JSON.stringify(updatedUnreadNotifications),
      );

      // Trigger storage event for other components to update
      this.triggerStorageEvent();
    } catch (error) {
      console.error('Error marking notifications as read:', error);
    }
  }

  /**
   * Mark all notifications as read by clearing the unread list
   */
  static markAllAsRead(): void {
    try {
      localStorage.setItem(this.UNREAD_NOTIFICATIONS_KEY, JSON.stringify([]));

      // Trigger storage event for other components to update
      this.triggerStorageEvent();
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
    }
  }

  /**
   * Set initial notifications (used during login)
   * @param allNotifications - All notifications to set
   * @param unreadNotifications - Unread notifications to set
   */
  static setInitialNotifications(
    allNotifications: Notification[],
    unreadNotifications: Notification[],
  ): void {
    try {
      localStorage.setItem(
        this.NOTIFICATIONS_KEY,
        JSON.stringify(allNotifications),
      );
      localStorage.setItem(
        this.UNREAD_NOTIFICATIONS_KEY,
        JSON.stringify(unreadNotifications),
      );

      // Trigger storage event for other components to update
      this.triggerStorageEvent();
    } catch (error) {
      console.error('Error setting initial notifications:', error);
    }
  }

  /**
   * Clear all notifications (used during logout)
   */
  static clearAllNotifications(): void {
    try {
      localStorage.removeItem(this.NOTIFICATIONS_KEY);
      localStorage.removeItem(this.UNREAD_NOTIFICATIONS_KEY);

      // Trigger storage event for other components to update
      this.triggerStorageEvent();
    } catch (error) {
      console.error('Error clearing notifications:', error);
    }
  }

  /**
   * Check if a notification is read by checking if it's not in the unread list
   * @param notificationId - The notification ID to check
   */
  static isNotificationRead(notificationId: string | number): boolean {
    const unreadNotifications = this.getUnreadNotifications();
    return !unreadNotifications.some(
      (notification) => notification.id == notificationId,
    );
  }

  /**
   * Get notifications with read status
   */
  static getNotificationsWithReadStatus(): (Notification & {
    isReadByUser?: boolean;
  })[] {
    const allNotifications = this.getAllNotifications();
    const unreadNotifications = this.getUnreadNotifications();
    const unreadIds = new Set(unreadNotifications.map((n) => n.id));

    return allNotifications.map((notification) => ({
      ...notification,
      isReadByUser: !unreadIds.has(notification.id),
    }));
  }

  /**
   * Subscribe to notification updates
   * @param callback - Function to call when notifications are updated
   * @returns Cleanup function
   */
  static subscribeToUpdates(
    callback: (data: {
      unreadCount: number;
      notifications: Notification[];
      unreadNotifications: Notification[];
    }) => void,
  ): () => void {
    const handler = (event: CustomEvent) => {
      callback(event.detail);
    };

    window.addEventListener('notificationsUpdated', handler as EventListener);

    // Return cleanup function
    return () => {
      window.removeEventListener(
        'notificationsUpdated',
        handler as EventListener,
      );
    };
  }

  /**
   * Private method to trigger storage event for components to update
   */
  private static triggerStorageEvent(): void {
    // Dispatch a custom event that components can listen to
    const notificationsWithReadStatus = this.getNotificationsWithReadStatus();
    const unreadNotifications = this.getUnreadNotifications();
    const unreadCount = this.getUnreadCount();

    window.dispatchEvent(
      new CustomEvent('notificationsUpdated', {
        detail: {
          unreadCount: unreadCount,
          notifications: notificationsWithReadStatus || [],
          unreadNotifications: unreadNotifications || [],
        },
      }),
    );
  }
}
